/**
 * Formula item-ref selectors — find itemId references inside a formula
 * string and compute the set difference against the publication's
 * current kit.
 *
 * The Formula node's `inputsConfig.formula.options` exposes per-kit-item
 * variables ( `quantitiesByItem`, `priceListsByItem`, `priceListsByQuantity` )
 * whose chip values include the itemId in a rison-style token, e.g.:
 *
 *     {{(source:quantitiesByItem,itemId:'a1b2c3')}}
 *     {{(source:priceListsByItem,itemId:'a1b2c3',priceListId:'pl-1')}}
 *
 * When the user removes an item from the kit, the chip stays referenced
 * in the saved formula but resolves to qty 0 at runtime. The rule editor
 * surfaces this via a yellow warning marker next to each affected chip and
 * a summary banner above the formula card. Both are informational only —
 * saving is not blocked.
 *
 * Selectors here are pure string/Set helpers — they do no DOM work and
 * have no React dependency, so they live in `state/` next to
 * `paletteVariables.ts`.
 */

/**
 * Source identifiers that carry an `itemId` in their rison token.
 *
 * These match the variable sources defined in
 * `publicationPriceFormulaUpdate.yaml` ( per the SDD ) — sources that bind
 * a chip to a specific kit item. Other sources ( e.g. `priceLists` aggregate,
 * `currencies`, `channelCommissions` ) are not item-keyed and are excluded.
 */
const ITEM_REF_SOURCES = [
  'quantitiesByItem',
  'priceListsByItem',
  'priceListsByQuantity'
] as const;

/**
 * Regex that matches an item-ref token's `itemId:'...'` portion.
 *
 * The formula string format is the engine's rison-style encoding wrapped
 * in `{{...}}`. Tokens look like:
 *
 *     {{(source:quantitiesByItem,itemId:'a1b2c3')}}
 *     {{(source:priceListsByItem,itemId:'a1b2c3',priceListId:'pl-1')}}
 *     {{(source:priceListsByQuantity,priceListId:'pl-1',itemId:'a1b2c3')}}
 *
 * The regex is intentionally permissive about token field order — it
 * looks for a `source:<one of three>` paired ( in either order, with any
 * fields between ) with `itemId:'<id>'`. Two patterns ( source-first and
 * itemId-first ) cover both orderings without depending on a full rison
 * parser.
 */
const SOURCE_PATTERN = ITEM_REF_SOURCES.join( '|' );
const ITEM_REF_TOKEN_REGEX = new RegExp(
  `source:(?:${SOURCE_PATTERN})[^)]*?itemId:'([^']+)'`
    + `|itemId:'([^']+)'[^)]*?source:(?:${SOURCE_PATTERN})`,
  'g'
);

/**
 * Extract the set of itemIds referenced by item-keyed variable tokens
 * inside a formula string.
 *
 * Acceptance criterion 1: parses `(source:(quantitiesByItem|priceListsByItem|priceListsByQuantity),itemId:'X',...)`
 * and returns the set of itemIds.
 *
 * Behavior:
 *   - Empty / non-string input returns an empty Set.
 *   - The same itemId referenced by multiple chips returns a Set with one
 *     entry ( natural Set semantics ).
 *   - Chips that omit `itemId` ( e.g. the `priceLists` aggregate or
 *     `currencies` ) are ignored — only the three kit-item sources match.
 *   - Token field order does not matter: both `source:X,itemId:'Y'` and
 *     `itemId:'Y',source:X` are recognised.
 */
export function formulaItemRefs( formulaString: string | null | undefined ): Set<string> {
  const refs = new Set<string>();
  if ( typeof formulaString !== 'string' || formulaString.length === 0 ) {
    return refs;
  }

  ITEM_REF_TOKEN_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while (( match = ITEM_REF_TOKEN_REGEX.exec( formulaString )) !== null ) {
    const itemId = match[ 1 ] ?? match[ 2 ];
    if ( itemId ) refs.add( itemId );
  }
  return refs;
}

/**
 * Compute the set difference `formulaItemRefs \ currentKitItemIds` —
 * itemIds the formula references that are no longer present in the
 * publication's current kit.
 *
 * Acceptance criterion 2: returns the set difference.
 *
 * Both inputs are accepted as either a `Set<string>` or an iterable
 * ( `string[]`, etc. ) so callers can pass either shape without a
 * conversion step.
 */
export function missingItemRefs(
  formulaRefs: Set<string> | Iterable<string>,
  currentKitItemIds: Set<string> | Iterable<string>
): Set<string> {
  const kitSet = currentKitItemIds instanceof Set
    ? currentKitItemIds
    : new Set( currentKitItemIds );
  const refsIterable: Iterable<string> = formulaRefs instanceof Set
    ? formulaRefs
    : new Set( formulaRefs );

  const missing = new Set<string>();
  for ( const id of refsIterable ) {
    if ( !kitSet.has( id )) missing.add( id );
  }
  return missing;
}
